import  { useState, useMemo, useEffect } from 'react'

/**
 * Video data for the library.
 * All cards show "CloseCarbon Basic Plan" / "5 mins" per the Figma export.
 * Swap `thumbnail` with your real image paths.
 */
const allVideos = [
  { id: 1, title: "Becoming a CloseCarbon Sub-Verifier", duration: "1.38 mins", videoId: "La7YpdVFFiQ" },
  { id: 2, title: "Closecarbon Verifier Plan", duration: "1.26 mins", videoId: "xK5PMog-SMA" },
  { id: 3, title: "CCRT Exchange Video", duration: "1.14 mins", videoId: "AXa5Wm9xf5I" },
  { id: 4, title: "CloseCarbon Basic Plan", duration: "1.34 mins", videoId: "yyxGaFNoDws" },
  { id: 5, title: "CloseCarbon Governance Plan", duration: "1.42 mins", videoId: "On9PHYLfZOk" },
  { id: 6, title: "CloseCarbon's Voluntary Donation Pool", duration: "1.20 mins", videoId: "zNU3i1cOjIY" },
  { id: 7, title: "Netzence - CloseCarbon Launch News By NTA", duration: "2.31 mins", videoId: "ONEaHjnVFLQ" },
]

function VideoCard({ title, duration, videoId }) {
  const [isPlaying, setIsPlaying] = useState(false)

  return (
    <div className="flex flex-col bg-[#FAFAFA] rounded-xl overflow-hidden transition-shadow duration-200 cursor-pointer group">
      {/* Thumbnail — 384:327 aspect keeps the full card at 431px at 378.67px width */}
      <div className="relative w-full aspect-[378.67/327] overflow-hidden bg-gray-200">
        {isPlaying ? (
          <iframe
            className="absolute inset-0 h-full w-full border-0"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <>
            <img
              src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/28" />
            <button
              type="button"
              onClick={() => setIsPlaying(true)}
              aria-label={`Play ${title}`}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[54px] h-[54px] flex items-center justify-center transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-white rounded-full"
            >
              <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20 15L38 27L20 39V15Z"
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Text block — p-5 + gap-4 = 104px tall */}
      <div className="flex flex-col gap-4 p-5">
        <h3
          className="text-[20px] lg:text-[24px] font-medium leading-[29px] text-[#141414]"
          style={{ fontFamily: "'SF Pro Display', sans-serif" }}
        >
          {title}
        </h3>
        <p
          className="text-[16px] font-medium leading-[19px] text-[#898989]"
          style={{ fontFamily: "'SF Pro Display', sans-serif" }}
        >
          {duration}
        </p>
      </div>
    </div>
  )
}

function WatchAllCloseCarbon() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('All')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [sortOrder, setSortOrder] = useState('Latest')

  const filterOptions = ['All', 'Basic Plan', 'Business Plan', 'Verifier', 'Governance']
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])

  const filteredVideos = useMemo(() => {
    return allVideos.filter((video) => {
      const matchesSearch = video.title
        .toLowerCase()
        .includes(searchQuery.trim().toLowerCase())
      const matchesFilter =
        selectedFilter === 'All' || video.title.includes(selectedFilter)
      return matchesSearch && matchesFilter
    })
  }, [searchQuery, selectedFilter])

  return (
    <>
      {/* ============ Page header ============ */}
      <section className="w-full bg-white px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[120px] pt-12 sm:pt-16 md:pt-20 lg:pt-24 xl:pt-[132px]">
        <div className="w-full max-w-[776px] flex flex-col gap-3">
          <h1
            className="text-[28px] sm:text-[34px] md:text-[40px] lg:text-[44px] xl:text-[48px] font-bold leading-[1.2] xl:leading-[57px] text-[#0D924F]"
            style={{ fontFamily: "'SF Pro Display', sans-serif" }}
          >
            CloseCarbon <span className="text-black">Video library </span>
          </h1>
          <p
            className="text-[14px] sm:text-[15px] md:text-[15px] xl:text-[16px] leading-[19px] text-[#3B3B3B]"
            style={{ fontFamily: "'SF Pro Display', sans-serif" }}
          >
            Subscribe to stay updated on latest carbon reduction initiatives, sustainable solutions, and environmental innovations.
          </p>
        </div>
      </section>

      {/* ============ Search + filter row ============ */}
      <section className="w-full bg-white px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[120px] pt-8 sm:pt-10 lg:pt-12 xl:pt-16">
        <div className="w-full max-w-[1200px] mx-auto flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-2">
          {/* Filter dropdown */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex flex-row items-center justify-center gap-1 px-3 py-4 w-full md:w-[100px] h-14 bg-[#FAFAFA] rounded-lg hover:bg-[#F2F2F2] transition-colors"
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen}
            >
              <span
                className="text-[15px] md:text-[16px] leading-[19px] text-[#006633] whitespace-nowrap"
                style={{ fontFamily: "'SF Pro Display', sans-serif" }}
              >
                {selectedFilter}
              </span>
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#898989"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isDropdownOpen && (
              <div
                role="listbox"
                className="absolute top-full left-0 mt-1 w-full md:w-[180px] bg-white rounded-lg shadow-lg border border-gray-200 z-20"
              >
                {filterOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={selectedFilter === option}
                    onClick={() => {
                      setSelectedFilter(option)
                      setIsDropdownOpen(false)
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-[#141414] hover:bg-[#FAFAFA] hover:text-[#006633] transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search input */}
          <div className="flex flex-row items-center gap-5 px-3 py-4 h-14 w-full md:flex-1 bg-[#F7F7F7] rounded-2xl">
            <div className="w-5 h-5 relative flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                  stroke="#B1B1B1"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search blog here..."
              className="w-full min-w-0 bg-transparent text-[14px] md:text-[16px] leading-[19px] text-[#141414] placeholder-[#B1B1B1] outline-none"
              style={{ fontFamily: "'SF Pro Display', sans-serif" }}
            />
          </div>
        </div>
      </section>

      {/* ============ Videos heading + grid ============ */}
      <section className="w-full bg-white px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[120px] pt-10 sm:pt-12 lg:pt-14 xl:pt-16 pb-12 sm:pb-16 lg:pb-20 xl:pb-[120px]">
        <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-8 lg:gap-10">
          {/* Heading */}
          <h2
            className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-medium leading-[1.2] lg:leading-[43px] text-[#141414]"
            style={{ fontFamily: "'SF Pro Display', sans-serif" }}
          >
            Videos
          </h2>

          {/* Video grid */}
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          ) : (
            <div className="w-full py-16 text-center">
              <p
                className="text-[16px] text-[#898989]"
                style={{ fontFamily: "'SF Pro Display', sans-serif" }}
              >
                No videos match your search.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default WatchAllCloseCarbon